import { useCallback, useRef, useState } from 'react';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  useForm,
} from '@carboledger/ui';
import { FileUp, X } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ATTACHMENTS } from '@/config/attachments.constants';
import { EMPTY_LIST, openFile, pickAttachments } from '@/lib/utils';
import styles from './AttachmentsField.module.scss';

// One file line — a stored file (keyed by id) or a just-picked File, each viewable and removable.
const AttachmentRow = ({ name, value, disabled, onView, onRemove }) => {
  const t = useTranslations();
  const handleView = useCallback(() => onView(value), [onView, value]);
  const handleRemove = useCallback(() => onRemove(value), [onRemove, value]);

  return (
    <li className={styles.item}>
      <Typography variant={TypographyVariants.bodySm} color={TypographyColors.primary}>
        {name}
      </Typography>
      <div className={styles.actions}>
        <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleView}>
          {t('COMMON_VIEW')}
        </Button>
        <button
          className={styles.remove}
          type="button"
          disabled={disabled}
          onClick={handleRemove}
          aria-label={t('FORM_REMOVE_FILE', { name })}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </li>
  );
};

// Form-bound: `attachments` (new File[]), `existingAttachments` ([{ id, filename }]) and `removeFileIds`.
/**
 * Props:
 *   disabled  locks the picker and every remove
 */
export const AttachmentsField = ({ disabled = false }) => {
  const t = useTranslations();
  const { values, setValue } = useForm();
  const inputRef = useRef(null);
  const [rejected, setRejected] = useState(EMPTY_LIST);

  const files = values.attachments ?? EMPTY_LIST;
  const existingFiles = values.existingAttachments ?? EMPTY_LIST;
  const removedFileIds = values.removeFileIds ?? EMPTY_LIST;

  const remainingExisting = existingFiles.filter((file) => !removedFileIds.includes(file.id));
  const maxNewFiles = Math.max(0, ATTACHMENTS.maxFiles - remainingExisting.length);

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  const handleInputChange = useCallback(
    (event) => {
      const chosen = Array.from(event.target.files ?? []);
      const result = pickAttachments(files, chosen, maxNewFiles);
      setRejected(result.rejected);
      setValue('attachments', result.accepted);
      // Clear the input so re-picking the same file still fires a change event.
      event.target.value = '';
    },
    [files, maxNewFiles, setValue],
  );

  const handleRemove = useCallback(
    (file) => {
      setValue(
        'attachments',
        files.filter((kept) => kept !== file),
      );
      setRejected(EMPTY_LIST);
    },
    [files, setValue],
  );

  // Removal is staged: the file stays on the server until the form is saved.
  const handleRemoveExisting = useCallback(
    (id) => setValue('removeFileIds', [...removedFileIds, id]),
    [removedFileIds, setValue],
  );

  const atLimit = files.length >= maxNewFiles;

  return (
    <div className={styles.field}>
      <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
        {t('FORM_ADD_ATTACHMENTS')}
      </Typography>

      <input
        ref={inputRef}
        className={styles.input}
        type="file"
        multiple
        accept={ATTACHMENTS.accept}
        onChange={handleInputChange}
        tabIndex={-1}
        aria-hidden="true"
      />

      <Button
        variant={ButtonVariants.secondary}
        size={ButtonSizes.md}
        disabled={disabled || atLimit}
        leftIcon={<FileUp />}
        onClick={openPicker}
      >
        {t('FORM_UPLOAD_FILES')}
      </Button>

      <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.placeholder}>
        {t('FORM_ATTACHMENTS_HINT')}
      </Typography>

      {remainingExisting.length > 0 && (
        <ul className={styles.list}>
          {remainingExisting.map((file) => (
            <AttachmentRow
              key={file.id}
              name={file.filename}
              value={file.id}
              disabled={disabled}
              onView={openFile}
              onRemove={handleRemoveExisting}
            />
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className={styles.list}>
          {files.map((file) => (
            <AttachmentRow
              key={`${file.name}-${file.size}-${file.lastModified}`}
              name={file.name}
              value={file}
              disabled={disabled}
              onView={openFile}
              onRemove={handleRemove}
            />
          ))}
        </ul>
      )}

      {rejected.map((entry) => (
        <Typography
          key={`${entry.name}-${entry.reason}`}
          variant={TypographyVariants.bodyXs}
          color={TypographyColors.error}
        >
          {t(entry.reason, { name: entry.name })}
        </Typography>
      ))}
    </div>
  );
};
